'use client';

import { Button } from 'shared/ui/button';
import { Separator } from 'shared/ui/separator';
import { Fragment, useCallback, useMemo, useState } from 'react';
import { Check } from 'lucide-react';
import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import type { z } from 'zod';
import type { IServiceSelect } from 'widgets/project-creation-stepper/model';
import { Form } from 'shared/ui/form';
import { ServiceProvider, useServiceType } from 'widgets/project-creation-stepper/providers/service-provider';
import { PostgresDetailsSchemaWithInstances } from '@/enteties/postgresql/model';
import { configSchema } from 'shared/types/configuration-service';
import { ConfigComponent } from 'widgets/project-creation-stepper/ui/config-component/config-component';
import { TariffComponent } from 'widgets/project-creation-stepper/ui/tariff-component';
import { ServiceComponent } from 'widgets/project-creation-stepper/ui/service-component';
import { UploadComponent } from 'widgets/project-creation-stepper/ui/upload-component';
import { useCreateApp } from 'widgets/project-creation-stepper/hooks/useCreateApp';
import { LoadingButton } from 'shared/ui/loading-button';
import { useApplyConfig } from 'widgets/project-creation-stepper/hooks/useApplyConfig';
import { useCreateCnpg } from 'widgets/project-creation-stepper/hooks/useCreateCnpg';
import { GitConfirm } from 'widgets/project-creation-stepper/ui/git-confirm';
import type { IStepper } from 'widgets/project-creation-stepper/providers/stepper-provider';
import { StepperProvider, useStep } from 'widgets/project-creation-stepper/providers/stepper-provider';
import { useCreateMarketplace } from 'widgets/project-creation-stepper/hooks/useCreateMarketplace';
import { mutationProjectKeys, projectKeys } from '@/enteties/project/api/projects.query-key';
import { useIsMutating, useQueryClient } from '@tanstack/react-query';
import { VariablesComponent } from 'widgets/project-creation-stepper/ui/varialbes-component';
import * as m from '@paraglide/messages';
export const ProjectCreationStepper = ({
  serviceType,
  projectType,
  marketplaceType,
  handleClose,
  stepper
}: {
  serviceType?: IServiceSelect['name'];
  projectType?: string;
  marketplaceType?: string;
  handleClose: () => void;
  stepper?: IStepper;
}) => {
  return <ServiceProvider initialState={{
    serviceType,
    selectedProjectType: projectType,
    selectedMarketplace: marketplaceType
  }} data-sentry-element="ServiceProvider" data-sentry-component="ProjectCreationStepper" data-sentry-source-file="project-creation-stepper.tsx">
      <StepperProvider initialState={stepper} data-sentry-element="StepperProvider" data-sentry-source-file="project-creation-stepper.tsx">
        <ProjectCreationSteps handleClose={handleClose} data-sentry-element="ProjectCreationSteps" data-sentry-source-file="project-creation-stepper.tsx" />
      </StepperProvider>
    </ServiceProvider>;
};
export const ProjectCreationSteps = ({
  handleClose
}: {
  handleClose: () => void;
}) => {
  const {
    useStepper,
    steps
  } = useStep();
  const stepper = useStepper();
  const {
    serviceType,
    selectedProjectType,
    uploadType,
    projectSlug,
    setFirstLoad,
    firstLoad,
    selectedMarketplace
  } = useServiceType();
  const [openGitDialog, setOpenGitDialog] = useState(false);
  const formResolver = useMemo(() => {
    if (stepper.current.id === 'tariff') return undefined;
    if (stepper.current.id === 'config') {
      if (serviceType === 'db') {
        if (selectedProjectType === 'cnpg') {
          return zodResolver(PostgresDetailsSchemaWithInstances);
        }
      } else if (serviceType === 'app') {
        return zodResolver(configSchema);
      }
    }
    return zodResolver(stepper.current.schema);
  }, [selectedProjectType, serviceType, stepper]);
  const form = useForm({
    mode: 'onTouched',
    resolver: formResolver,
    defaultValues: {
      name: ''
    }
  });
  const goToAndSetFieldError = useCallback((step: 'tariff' | 'service-type', name: string, error: string) => {
    stepper.goTo(step);
    setTimeout(() => {
      form.setError(name, {
        message: error
      }, {
        shouldFocus: true
      });
    }, 200);
  }, [form, stepper]);
  const {
    mutate,
    timedOut
  } = useCreateApp(stepper.next);
  const isMutating = useIsMutating({
    mutationKey: mutationProjectKeys.validateProject(projectSlug)
  });
  const {
    applyConfig,
    isPending
  } = useApplyConfig(handleClose);
  const {
    mutate: createCnpg,
    timedOut: cnpgTimedOut
  } = useCreateCnpg(handleClose, goToAndSetFieldError);
  const marketPlace = useCreateMarketplace(handleClose, goToAndSetFieldError);
  const queryClient = useQueryClient();
  const onSubmit = (values: z.infer<typeof stepper.current.schema>) => {
    const allValues = form.getValues();
    if (stepper.isLast) {
      if (serviceType === 'app') {
        if (uploadType === 'interface') {
          // @ts-ignore
          applyConfig.mutate({
            slug: projectSlug,
            config: allValues
          });
        } else {
          setOpenGitDialog(true);
        }
      }
      if (serviceType === 'db' && selectedProjectType === 'cnpg') {
        // @ts-ignore
        createCnpg(allValues);
      }
      if (serviceType === 'marketplace') {
        // @ts-ignore
        marketPlace.mutate(allValues);
      }
      // console.log('All form values: ', allValues);
    } else if (stepper.current.id === 'tariff') {
      if (serviceType === 'app') {
        // @ts-ignore
        mutate(values);
      } else {
        stepper.next();
      }
    } else {
      if (stepper.isFirst && firstLoad) setFirstLoad(false);
      stepper.next();
    }
  };
  const isMarketplaceDisabled = useMemo(() => serviceType === 'marketplace' && (!serviceType || !selectedProjectType || !selectedMarketplace), [selectedMarketplace, selectedProjectType, serviceType]);
  const isDisabled = useMemo(() => stepper.current.id === 'service-type' && serviceType !== 'app' && (!serviceType || !selectedProjectType) || isMarketplaceDisabled, [isMarketplaceDisabled, selectedProjectType, serviceType, stepper]);
  const gitConfirmClose = useCallback(() => {
    queryClient.invalidateQueries({
      queryKey: projectKeys.allProjects
    }).then(() => {
      handleClose();
    });
  }, [queryClient, handleClose]);
  const handleBack = useCallback(() => {
    if (stepper.current.id === 'config' && (serviceType === 'db' || serviceType === 'marketplace')) {
      stepper.goTo('tariff');
    } else {
      stepper.prev();
    }
  }, [serviceType, stepper]);
  const handleStepClick = useCallback((id: string, index: number) => {
    if (index <= stepper.current.index) {
      // @ts-ignore
      stepper.goTo(id);
    }
  }, [stepper]);
  const isLoading = useMemo(() => timedOut || cnpgTimedOut || isPending || marketPlace.isPending || !!isMutating, [cnpgTimedOut, isPending, marketPlace.isPending, timedOut, isMutating]);
  return <Form {...form} data-sentry-element="Form" data-sentry-component="ProjectCreationSteps" data-sentry-source-file="project-creation-stepper.tsx">
      <form onSubmit={form.handleSubmit(onSubmit)} className="">
        <div className="space-y-4 rounded-lg">
          <nav aria-label="Checkout Steps" className="group my-4 px-3">
            <ol className="flex items-center justify-between gap-1 sm:gap-2 text-sm sm:text-base" aria-orientation="horizontal">
              {stepper.all.map((step, index, array) => <Fragment key={step.id}>
                  <li className="flex items-center gap-1 sm:gap-2 shrink-0">
                    <Button type="button" role="tab" variant={index <= stepper.current.index ? 'default' : 'stepperMuted'} aria-current={stepper.current.id === step.id ? 'step' : undefined} aria-posinset={index + 1} aria-setsize={steps.length} aria-selected={stepper.current.id === step.id} className="flex size-6 items-center justify-center rounded-full p-0 text-title4 font-normal" onClick={() => handleStepClick(step.id, index)}>
                      {index >= stepper.current.index ? index + 1 : <Check className="size-3" />}
                    </Button>
                    <span className="text-xs sm:text-sm hidden md:inline-block font-medium whitespace-nowrap">
                      {step.title}
                    </span>
                  </li>
                  {index < array.length - 1 && <Separator className={`flex-1 ${index < stepper.current.index ? 'bg-primary' : 'bg-muted-stepper'}`} />}
                </Fragment>)}
            </ol>
          </nav>

          <div className="space-y-4 px-3 w-full">
            {stepper.switch({
            'service-type': () => <ServiceComponent next={stepper.next} />,
            tariff: () => <TariffComponent />,
            upload: () => <UploadComponent next={stepper.next} />,
            variables: () => <VariablesComponent next={stepper.next} />,
            config: () => <ConfigComponent />
          })}
          </div>

          <div className="flex justify-between px-3">
            <Button type="button" variant="outline" onClick={handleBack} disabled={stepper.isFirst} size="sm" data-sentry-element="Button" data-sentry-source-file="project-creation-stepper.tsx">
              {m.create_dialog_step_back_btn()}
            </Button>
            <div className="flex justify-end gap-3">
              <Button variant="outline" onClick={handleClose} size="sm" type="button" data-sentry-element="Button" data-sentry-source-file="project-creation-stepper.tsx">
                {m.btn_cancel()}
              </Button>
              <LoadingButton loading={isLoading} type="submit" size="sm" disabled={isDisabled} data-sentry-element="LoadingButton" data-sentry-source-file="project-creation-stepper.tsx">
                {stepper.isLast ? m.create_dialog_step_fin_btn() : m.create_dialog_step_next_btn()}
              </LoadingButton>
            </div>
            {openGitDialog && <GitConfirm open={openGitDialog} setOpen={setOpenGitDialog} onConfirm={gitConfirmClose} />}
          </div>
        </div>
      </form>
    </Form>;
};